import React, { useState, useEffect } from 'react';
import { Search, ShoppingCart, Plus, BarChart3, Package, Truck, Wrench, Camera, Heart, XCircle, Calendar, Building2, FolderTree, Palette } from 'lucide-react';
import { useToast } from './components/ui/ToastContext';
import { ThemeToggle, Button, FormGroup, BottomNavigation } from './components/ui';
import LanguageToggle from './components/ui/LanguageToggle';
import { LandingPage } from './components/landing';
import NotificationCenter from './components/ui/NotificationCenter';
import useNotifications from './hooks/useNotifications';
import AddPropModal from './components/modals/AddPropModal';
import PropDetailsModal from './components/modals/PropDetailsModal';
import TransferModal from './components/modals/TransferModal';
import CartView from './components/views/CartView';
import SearchView from './components/views/SearchView';
import SearchV2 from './components/SearchV2';
import InventoryView from './components/views/InventoryView';
import ServiceInventoryView from './components/views/ServiceInventoryView';
import TransfersView from './components/views/TransfersView';
import AddPropView from './components/views/AddPropView';
import MyRequestsView from './components/views/MyRequestsView';
import ReservationsView from './components/views/ReservationsView';
import ListsView from './components/views/ListsView';
import DatabaseDashboardView from './components/views/DatabaseDashboardView';
import AnalyticsView from './components/views/AnalyticsView';
import OrganizationManagementView from './components/views/OrganizationManagementView';
import DatabaseSetupView from './components/views/DatabaseSetupView';
import CategoryManagementView from './components/views/CategoryManagementView';
import UISetupView from './components/views/UISetupView';
import WasabiMigrationTool from './components/WasabiMigrationTool';
import WasabiCleanupTool from './components/WasabiCleanupTool';
import useSupabase from './hooks/useSupabase';
import { supabase } from './supabaseClient';

const defaultGeneralUser = {
  id: 1,
  name: "alex_johnson",
  role: "general",
  organization: "Downtown Theatre Collective",
  location: "Austin, TX"
};

const buildDefaultUser = (overrideRole) => ({
  ...defaultGeneralUser,
  role: overrideRole || defaultGeneralUser.role
});

const buildCurrentUserFromSupabase = (user, overrideRole = null) => {
  if (!user) return null;
  const metadata = user.user_metadata || {};
  const fallbackName = metadata.full_name || metadata.name || user.email?.split('@')[0];
  const resolvedRole = overrideRole || metadata.role || 'general';
  return {
    id: user.id,
    name: fallbackName || 'Propshare Member',
    role: resolvedRole,
    organization: metadata.organization || 'Propshare Network',
    location: metadata.location || metadata.city || 'Austin, TX',
    email: user.email,
  };
};

const PropKioskDashboard = ({ roleParam }) => {
  const { toast } = useToast();
  
  // Add state for showing landing page
  const [showLandingPage, setShowLandingPage] = useState(false);
  
  const [currentUser, setCurrentUser] = useState(() => buildDefaultUser(
    roleParam === 'warehouse_admin' ? 'warehouse_admin' : undefined
  ));
  const [supabaseUser, setSupabaseUser] = useState(null);
  
  const {
    notifications,
    markAsRead,
    clearAll,
    notifyReservationSubmitted,
    notifyReservationApproved,
    notifyReservationRejected,
    notifyReservationReady
  } = useNotifications(currentUser);
  
  const { 
    loading, 
    error, 
    fetchInitialData,
    fetchUserLists,
    createUserList,
    addPropToList,
    removePropFromList,
    deleteUserList,
    requestTransfer, 
    saveProp, 
    deleteProp, 
    checkoutProps, 
    returnProps,
    submitReservationRequest,
    fetchReservations,
    updateReservationStatus,
    completeReservation,
    fetchCategories,
    getCategoryAndSubcategoriesById,
    debugCategoryCounts
  } = useSupabase();

  const [activeView, setActiveView] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    category: '',
    location: '',
    availability: 'all',
    tags: [],
    maxDistance: 2800,
    minDistance: 0
  });

  // Database state
  const [props, setProps] = useState([]);
  const [propsLoaded, setPropsLoaded] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transferRequests, setTransferRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [userLists, setUserLists] = useState([]);
  // State for valid category IDs (including subcategories)
  const [validCategoryIds, setValidCategoryIds] = useState([]);

  // UI state
  const [cart, setCart] = useState([]);
  const [selectedProp, setSelectedProp] = useState(null);
  const [recommendedProp, setRecommendedProp] = useState(null); // For recommendation clicks
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferProp, setSelectedTransferProp] = useState(null);
  const [showAddPropModal, setShowAddPropModal] = useState(false);
  const [editingProp, setEditingProp] = useState(null);
  const [propForm, setPropForm] = useState({
    name: '',
    category_id: '', // Changed from category to category_id
    length: '',
    width: '',
    height: '',
    weight: '',
    condition: '',
    material: [],
    tags: [],
    colors: [],
    size: 'medium',
    usage_category_id: '',
    use_how: '',
    use_when: '',
    use_where: '',
    historical_period: '',
    historical_region: '',
    verified: false,
    retention_agreement: false,
    modification_allowed: true,
    transfer_available: true,
    is_consumable: false,
    consumable_policy: 'replace',
    private_notes: '',
    barcode: '',
    purchase_date: '',
    status: 'available',
    checked_in: true,
    images: []
  });

  // Fetch data from Supabase
  useEffect(() => {
    loadInitialData();
    
    // Expose debug function globally for console access
    window.debugCategoryCounts = debugCategoryCounts;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    const syncSupabaseSession = async () => {
      try {
      const { data } = await supabase.auth.getSession();
      if (!isMounted) return;
      const user = data?.session?.user ?? null;
      setSupabaseUser(user);
      if (user) {
        setCurrentUser(buildCurrentUserFromSupabase(user, roleParam));
      }
    } catch (error) {
      console.error('Error checking Supabase session for dashboard:', error);
    }
    };

    syncSupabaseSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      const user = session?.user ?? null;
      setSupabaseUser(user);
      if (user) {
        setCurrentUser(buildCurrentUserFromSupabase(user, roleParam));
      } else {
        setCurrentUser(defaultGeneralUser);
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription?.unsubscribe();
    };
  }, [roleParam]);

  useEffect(() => {
    if (roleParam === 'warehouse_admin') {
      setActiveView('inventory');
    }
  }, [roleParam]);

  useEffect(() => {
    if (supabaseUser) return;
    const overrideRole = roleParam === 'warehouse_admin' ? 'warehouse_admin' : undefined;
    setCurrentUser(buildDefaultUser(overrideRole));
  }, [roleParam, supabaseUser]);

  // Update valid category IDs when selected category changes
  useEffect(() => {
    const updateValidCategoryIds = async () => {
      if (selectedFilters.category) {
        try {
          const categoryIds = await getCategoryAndSubcategoriesById(selectedFilters.category);
          setValidCategoryIds(categoryIds);
          console.log('🔍 Valid category IDs for filtering:', categoryIds);
        } catch (err) {
          console.error('Error getting category IDs:', err);
          setValidCategoryIds([selectedFilters.category]); // Fallback to just the selected category
        }
      } else {
        setValidCategoryIds([]);
      }
    };
    
    updateValidCategoryIds();
  }, [selectedFilters.category, getCategoryAndSubcategoriesById]);

  // Update valid categories for filtering when selected category changes
  // Note: Keeping this for legacy support
  useEffect(() => {
    console.log('🔍 Selected category filter (ID):', selectedFilters.category);
  }, [selectedFilters.category]);

  const loadInitialData = async () => {
    try {
      setPropsLoaded(false);
      const data = await fetchInitialData();
      setProps(data.props);
      setPropsLoaded(true);
      setOrganizations(data.organizations);
      setTransferRequests(data.transferRequests);
      
      // Also load categories
      const categoriesData = await fetchCategories();
      setCategories(categoriesData);
      
      // Also load reservations
      const reservationsData = await fetchReservations(currentUser);
      setReservations(reservationsData);
      
      // Load user lists from database
      const listsData = await fetchUserLists(currentUser);
      setUserLists(listsData);
    } catch (err) {
      console.error('Error during loadInitialData:', err);
    }
  };

  const handleRequestTransfer = (prop) => {
    setSelectedTransferProp(prop);
    setShowTransferModal(true);
  };

  const handleTransferSubmit = async (propId) => {
    try {
      await requestTransfer(propId, "", currentUser, organizations, props);
      toast.success('Transfer request submitted successfully!');
      loadInitialData();
      setShowTransferModal(false);
      setSelectedTransferProp(null);
    } catch (err) {
      toast.error('Failed to submit transfer request', { title: 'Transfer Error' });
    }
  };

  const handleSaveProp = async () => {
    try {
      console.log('🔄 Starting prop save...');
      console.log('📸 Form images before save:', propForm.images);
      await saveProp(propForm, editingProp, currentUser, organizations);
      toast.success(editingProp ? 'Prop updated successfully!' : 'Prop added successfully!');
      
      // Refresh the data to see the changes
      loadInitialData();
      
      setShowAddPropModal(false);
      
      // Keep form data intact - don't reset
      console.log('✅ Prop saved! Form data preserved.');
    } catch (err) {
      console.error('❌ Save error:', err);
      toast.error('Failed to save prop', { title: 'Save Error' });
    }
  };

  const handleDeleteProp = async (propId) => {
    if (!window.confirm('Are you sure you want to delete this prop? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteProp(propId);
      toast.success('Prop deleted successfully!');
      loadInitialData();
    } catch (err) {
      toast.error('Failed to delete prop', { title: 'Delete Error' });
    }
  };

  const handleCheckoutProps = async (propIds, expectedReturnDate, purpose) => {
    try {
      await checkoutProps(propIds, currentUser, expectedReturnDate, purpose);
      toast.success(`Successfully checked out ${propIds.length} prop(s)!`);
      
      // Only remove the checked-out props from cart, keep the rest
      const remainingCart = cart.filter(id => !propIds.includes(id));
      setCart(remainingCart);
      
      loadInitialData(); // Refresh data
    } catch (err) {
      toast.error('Failed to checkout props', { title: 'Checkout Error' });
    }
  };

  // Enhanced reservation submission handler
  const handleSubmitReservation = async (propIds, expectedReturnDate, purpose, notes, options = {}) => {
    try {
      await submitReservationRequest(propIds, currentUser, expectedReturnDate, purpose, notes, options);
      
      // Show toast for immediate feedback
      toast.success(`Reservation request submitted for ${propIds.length} prop(s)!`);
      
      // Add persistent notification
      const targetOrgs = [...new Set(propIds.map(id => {
        const prop = props.find(p => p.id === id);
        return prop?.owner || 'Unknown Organization';
      }))];
      
      notifyReservationSubmitted(propIds.length, targetOrgs.join(', '));
      
      // Clear the submitted props from cart
      const remainingCart = cart.filter(id => !propIds.includes(id));
      setCart(remainingCart);
      
      loadInitialData(); // Refresh data
    } catch (err) {
      toast.error('Failed to submit reservation', { title: 'Reservation Error' });
    }
  };

  const handleSaveAsListFromCart = async (propIds, listName, listDescription) => {
    try {
      const newList = await createUserList({
        name: listName,
        description: listDescription
      }, currentUser);
      
      // Add all props to the list
      for (const propId of propIds) {
        await addPropToList(propId, newList.id);
      }
      
      // Update the list with the prop IDs
      newList.propIds = propIds;
      setUserLists([...userLists, newList]);
      toast.success(`List "${listName}" created with ${propIds.length} props!`);
    } catch (err) {
      toast.error('Failed to create list', { title: 'List Error' });
    }
  };

  const handleCreateEmptyList = async (listData) => {
    try {
      const newList = await createUserList(listData, currentUser);
      setUserLists([...userLists, newList]);
      return newList;
    } catch (err) {
      throw err;
    }
  };

  const handleAddToList = async (propId, listId) => {
    try {
      await addPropToList(propId, listId);
      
      // Update local state
      setUserLists(userLists.map(list => {
        if (list.id === listId) {
          const updatedPropIds = [...list.propIds];
          if (!updatedPropIds.includes(propId)) {
            updatedPropIds.push(propId);
          }
          return {
            ...list,
            propIds: updatedPropIds,
            updatedAt: new Date()
          };
        }
        return list;
      }));
      toast.success('Added prop to list');
    } catch (err) {
      toast.error('Failed to add prop to list', { title: 'List Error' });
    }
  };

  const handleReturnProps = async (propIds) => {
    if (!window.confirm(`Are you sure you want to return ${propIds.length} prop(s)?`)) {
      return;
    }

    try {
      console.log('🔄 Starting return process for props:', propIds);
      await returnProps(propIds);
      console.log('✅ Return process completed');
      
      toast.success(`Successfully returned ${propIds.length} prop(s)!`);
      
      // Add a small delay to ensure database updates have completed
      setTimeout(() => {
        console.log('🔄 Refreshing data after return...');
        loadInitialData();
      }, 1000);
    } catch (err) {
      console.error('❌ Error in handleReturnProps:', err);
      toast.error('Failed to return props', { title: 'Return Error' });
    }
  };

  // UI helper functions
  const filteredProps = (!propsLoaded || !props || props.length === 0) ? [] : props.filter(prop => {
    try {
      // Search query filter - enhanced to search multiple fields
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = prop.name.toLowerCase().includes(query);
        const matchesTags = prop.tags && Array.isArray(prop.tags) && prop.tags.some(tag => tag.toLowerCase().includes(query));
        const matchesMaterial = prop.material && Array.isArray(prop.material) && prop.material.some(material => material.toLowerCase().includes(query));
        const matchesPeriod = prop.historical_period && prop.historical_period.toLowerCase().includes(query);
        const matchesRegion = prop.historical_region && prop.historical_region.toLowerCase().includes(query);
        
        if (!matchesName && !matchesTags && !matchesMaterial && !matchesPeriod && !matchesRegion) {
          return false;
        }
      }

      // Category filter - now working with hierarchical category IDs
      if (selectedFilters.category) {
        if (selectedFilters.category === 'uncategorized') {
          // Special case: show only uncategorized props
          if (prop.category_id && prop.category !== 'Uncategorized') {
            return false;
          }
        } else {
          // Handle uncategorized props - they should only show when no category is selected
          if (!prop.category_id || prop.category === 'Uncategorized') {
            // Show uncategorized props only when "All Categories" is selected (empty string)
            if (selectedFilters.category !== '') {
              return false;
            }
          } else {
            // For categorized props, check if their category_id is in the list of valid category IDs
            // This includes the selected category AND all its subcategories
            if (validCategoryIds.length > 0 && !validCategoryIds.includes(prop.category_id)) {
              return false;
            }
          }
        }
      }

      // Availability filter
      if (selectedFilters.availability === 'available' && prop.status !== 'available') {
        return false;
      }
      if (selectedFilters.availability === 'checked_out' && prop.status !== 'checked_out') {
        return false;
      }

      // Distance filter - only filter if distance is actually calculated
      if (selectedFilters.maxDistance && prop.distance != null && prop.distance > selectedFilters.maxDistance) {
        return false;
      }
      if (selectedFilters.minDistance && prop.distance != null && prop.distance < selectedFilters.minDistance) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error filtering prop:', prop.id, error);
      return false;
    }
  });

  // Debug logging for filtered results
  if (selectedFilters.category) {
    console.log(`🔍 Search Results - Category: ${selectedFilters.category}, Filtered Props: ${filteredProps.length}, Total Props: ${props.length}`);
  }

  const addToCart = (propId) => {
    // Check if prop is already in cart
    if (cart.includes(propId)) {
      console.log('⚠️ Prop already in cart:', propId);
      return;
    }
    
    // Check if prop is already reserved
    const prop = props.find(p => p.id === propId);
    if (prop && prop.status === 'reserved') {
      toast.warning('This prop is already reserved by another user.');
      return;
    }
    
    console.log('➕ Adding prop to cart:', propId);
    setCart([...cart, propId]);
    setProps(props.map(p => p.id === propId ? {...p, inCart: true} : p));
  };

  const removeFromCart = (propId) => {
    setCart(cart.filter(id => id !== propId));
    setProps(props.map(p => p.id === propId ? {...p, inCart: false} : p));
  };

  const clearFilters = () => {
    setSelectedFilters({
      category: '',
      location: '',
      availability: 'all',
      tags: [],
      maxDistance: 2800,
      minDistance: 0
    });
    setSearchQuery('');
  };

  const resetPropForm = () => {
    setPropForm({
      name: '',
      category_id: '', // Changed from category to category_id
      length: '',
      width: '',
      height: '',
      weight: '',
      condition: '',
      material: [],
      tags: [],
      colors: [],
      size: 'medium',
      usage_category_id: '',
      use_how: '',
      use_when: '',
      use_where: '',
      historical_period: '',
      historical_region: '',
      verified: false,
      retention_agreement: false,
      modification_allowed: true,
      transfer_available: true,
      is_consumable: false,
      consumable_policy: 'replace',
      private_notes: '',
      barcode: '',
      purchase_date: '',
      status: 'available',
      checked_in: true,
      images: []
    });
    setEditingProp(null);
  };

  const openAddProp = () => {
    resetPropForm();
    setShowAddPropModal(true);
  };

  const openEditProp = async (prop) => {
    console.log('📋 Opening edit for prop:', prop.name);
    console.log('📸 Prop images array:', prop.images);
    
    // Fetch fresh data for this prop
    try {
      const { data: freshProp, error } = await supabase
        .from('props')
        .select(`
          *,
          category_info:categories!props_category_id_fkey(name)
        `)
        .eq('id', prop.id)
        .single();
      
      if (error) throw error;
      
      console.log('🔄 Fresh prop data loaded:', freshProp.images);
      
      const propToEdit = freshProp || prop;
      setPropForm({
        name: propToEdit.name || '',
        category_id: propToEdit.category_id || '', // Use category_id directly from database
        length: propToEdit.length || '',
        width: propToEdit.width || '',
        height: propToEdit.height || '',
        weight: propToEdit.weight || '',
        condition: propToEdit.condition || '',
        material: Array.isArray(propToEdit.material) ? propToEdit.material : [],
        tags: Array.isArray(propToEdit.tags) ? propToEdit.tags : [],
        colors: Array.isArray(propToEdit.colors) ? propToEdit.colors : (propToEdit.colors ? [propToEdit.colors] : []),
        size: propToEdit.size || 'medium',
        usage_category_id: propToEdit.usage_category_id || '',
        use_how: propToEdit.use_how || '',
        use_when: propToEdit.use_when || '',
        use_where: propToEdit.use_where || '',
        historical_period: propToEdit.historical_period || '',
        historical_region: propToEdit.historical_region || '',
        verified: propToEdit.verified || false,
        retention_agreement: propToEdit.retention_agreement || false,
        modification_allowed: propToEdit.modification_allowed !== false,
        transfer_available: propToEdit.transfer_available !== false,
        is_consumable: propToEdit.is_consumable || false,
        consumable_policy: propToEdit.consumable_policy || 'replace',
        private_notes: propToEdit.private_notes || '',
        barcode: propToEdit.barcode || '',
        purchase_date: propToEdit.purchase_date || '',
        status: propToEdit.status || 'available',
        checked_in: propToEdit.checked_in !== false,
        images: Array.isArray(propToEdit.images) ? propToEdit.images : []
      });
      console.log('✨ Form images set to:', Array.isArray(propToEdit.images) ? propToEdit.images : []);
      setEditingProp(propToEdit);
    } catch (err) {
      console.error('Error fetching fresh prop data:', err);
      // Fallback to existing prop data
      setPropForm({
        name: prop.name || '',
        category_id: prop.category_id || '', // Use category_id directly
        length: prop.length || '',
        width: prop.width || '',
        height: prop.height || '',
        weight: prop.weight || '',
        condition: prop.condition || '',
        material: Array.isArray(prop.material) ? prop.material : [],
        tags: Array.isArray(prop.tags) ? prop.tags : [],
        colors: Array.isArray(prop.colors) ? prop.colors : (prop.colors ? [prop.colors] : []),
        size: prop.size || 'medium',
        usage_category_id: prop.usage_category_id || '',
        use_how: prop.use_how || '',
        use_when: prop.use_when || '',
        use_where: prop.use_where || '',
        historical_period: prop.historical_period || '',
        historical_region: prop.historical_region || '',
        verified: prop.verified || false,
        retention_agreement: prop.retention_agreement || false,
        modification_allowed: prop.modification_allowed !== false,
        transfer_available: prop.transfer_available !== false,
        is_consumable: prop.is_consumable || false,
        consumable_policy: prop.consumable_policy || 'replace',
        private_notes: prop.private_notes || '',
        barcode: prop.barcode || '',
        purchase_date: prop.purchase_date || '',
        status: prop.status || 'available',
        checked_in: prop.checked_in !== false,
        images: Array.isArray(prop.images) ? prop.images : []
      });
      setEditingProp(prop);
    }
    
    setShowAddPropModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PropShare Network...</p>
          <p className="text-sm text-gray-500 mt-2">Connecting to Supabase database...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Database Connection Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            onClick={loadInitialData}
            variant="primary"
            size="normal"
          >
            Retry Connection
          </Button>
        </div>
      </div>
    );
  }

  const renderActiveView = () => {
    // Role-based view protection
    const isAuthorized = (viewId, userRole) => {
      const rolePermissions = {
        general: ['search', 'searchv2', 'requests', 'lists', 'cart'],
        warehouse_admin: ['inventory', 'reservations', 'add-prop', 'analytics', 'transfers'],
        service_provider: ['search', 'database', 'service-inventory', 'organizations', 'categories', 'wasabi-migration', 'wasabi-cleanup', 'wasabi-renewal', 'ui-setup', 'setup', 'services', 'delivery', 'digitization']
      };
      
      return rolePermissions[userRole]?.includes(viewId) || false;
    };
    
    // If current view is not authorized for current role, redirect
    if (!isAuthorized(activeView, currentUser.role)) {
      const defaultViews = {
        general: 'search',
        warehouse_admin: 'inventory', 
        service_provider: 'database'
      };
      
      const defaultView = defaultViews[currentUser.role] || 'search';
      setActiveView(defaultView);
      return (
        <div className="text-center py-12 text-red-500">
          <p className="text-lg mb-2">⚠️ Unauthorized Access</p>
          <p className="text-sm">Redirecting to {defaultView}...</p>
        </div>
      );
    }

    switch(activeView) {
      case 'search':
        return (
          <SearchView
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            filteredProps={filteredProps}
            props={props}
            organizations={organizations}
            categories={categories}
            transferRequests={transferRequests}
            clearFilters={clearFilters}
            addToCart={addToCart}
            removeFromCart={removeFromCart}
            setSelectedProp={setSelectedProp}
            handleRequestTransfer={handleRequestTransfer}
            userLists={userLists}
            onAddToList={handleAddToList}
          />
        );

      case 'searchv2':
        return <SearchV2 />;

      case 'cart':
        return (
          <CartView
            props={props}
            cart={cart}
            setCart={setCart}
            organizations={organizations}
            currentUser={currentUser}
            setActiveView={setActiveView}
            removeFromCart={removeFromCart}
            onCheckoutProps={handleCheckoutProps}
            onSubmitReservation={handleSubmitReservation}
            onSaveAsListFromCart={handleSaveAsListFromCart}
          />
        );

      case 'lists':
        return (
          <ListsView
            currentUser={currentUser}
            props={props}
            addToCart={addToCart}
            userLists={userLists}
            setUserLists={setUserLists}
            onCreateList={handleCreateEmptyList}
            onRemoveFromList={removePropFromList}
            onDeleteList={deleteUserList}
            refreshLists={() => fetchUserLists(currentUser).then(setUserLists)}
            cart={cart}
            setCart={setCart}
            setProps={setProps}
          />
        );

      case 'reservations':
        return (
          <ReservationsView
            currentUser={currentUser}
            fetchReservations={fetchReservations}
            updateReservationStatus={updateReservationStatus}
            completeReservation={completeReservation}
            props={props}
            onDataChange={loadInitialData}
            onNotifyUser={(type, data) => {
              switch(type) {
                case 'reservation_approved':
                  notifyReservationApproved(data.propCount, data.orgName, data.pickupDate, data.targetUser);
                  break;
                case 'reservation_rejected':
                  notifyReservationRejected(data.propCount, data.orgName, data.reason, data.targetUser);
                  break;
                case 'reservation_ready':
                  notifyReservationReady(data.propCount, data.orgName, data.targetUser);
                  break;
                default:
                  break;
              }
            }}
          />
        );

      case 'database':
        return (
          <DatabaseDashboardView
            props={props}
            organizations={organizations}
            transferRequests={transferRequests}
            userLists={userLists}
            reservations={reservations}
            setActiveView={setActiveView}
          />
        );

      case 'service-inventory':
        return (
          <ServiceInventoryView
            props={props}
            currentUser={currentUser}
            openAddProp={openAddProp}
            openEditProp={openEditProp}
            setSelectedProp={setSelectedProp}
            onDataChange={loadInitialData}
          />
        );

      case 'organizations':
        return (
          <OrganizationManagementView
            currentUser={currentUser}
          />
        );

      case 'categories':
        return (
          <CategoryManagementView
            currentUser={currentUser}
          />
        );

      case 'wasabi-migration':
        return <WasabiMigrationTool />;

      case 'wasabi-cleanup':
        return <WasabiCleanupTool />;

      case 'wasabi-renewal':
        return (
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold mb-2">URL Renewal Tool</h1>
              <p className="text-gray-600">
                Refresh expiring Wasabi signed URLs to maintain image access
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-600 mb-4">
                This tool will scan your database for Wasabi URLs that are expiring soon and generate fresh signed URLs.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  🚧 URL Renewal functionality is coming soon! For now, new uploads get 30-day expiration instead of 7 days.
                </p>
              </div>
            </div>
          </div>
        );

      case 'ui-setup':
        return <UISetupView />;

      case 'setup':
        return <DatabaseSetupView />;

      case 'requests':
        return (
          <MyRequestsView
            currentUser={currentUser}
            fetchReservations={fetchReservations}
            updateReservationStatus={updateReservationStatus}
            props={props}
            onDataChange={loadInitialData}
          />
        );

      case 'transfers':
        return <TransfersView transferRequests={transferRequests} />;

      case 'analytics':
        return (
          <AnalyticsView
            currentUser={currentUser}
            props={props}
            reservations={reservations}
            transferRequests={transferRequests}
            organizations={organizations}
          />
        );

      case 'add-prop':
        return (
          <AddPropView 
            setActiveView={setActiveView} 
            openAddProp={openAddProp} 
            saveProp={saveProp}
            currentUser={currentUser}
            organizations={organizations}
          />
        );

      case 'inventory':
        return (
          <InventoryView
            props={props}
            currentUser={currentUser}
            openAddProp={openAddProp}
            openEditProp={openEditProp}
            setSelectedProp={setSelectedProp}
            onReturnProps={handleReturnProps}
            onDataChange={loadInitialData}
          />
        );

      default:
        return <div className="text-center py-12 text-gray-500">Select a view from the navigation</div>;
    }
  };

  // Get pending reservations count for current user organization
  const pendingReservationsCount = reservations.filter(r => 
    r.status === 'pending' && 
    r.target_org?.name === currentUser.organization
  ).length;

  const menuItems = {
    general: [
      { id: 'search', label: 'Search Props', icon: Search },
      { id: 'searchv2', label: 'Search V2', icon: Search },
      { id: 'requests', label: 'My Requests', icon: Calendar },
      { id: 'lists', label: 'My Lists', icon: Heart },
      { id: 'cart', label: `Cart (${cart.length})`, icon: ShoppingCart }
    ],
    warehouse_admin: [
      { id: 'inventory', label: 'Manage Inventory', icon: Package },
      { id: 'reservations', label: `Reservations${pendingReservationsCount > 0 ? ` (${pendingReservationsCount})` : ''}`, icon: Calendar },
      { id: 'add-prop', label: 'Add New Prop', icon: Plus },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
      { id: 'transfers', label: 'Transfers', icon: Truck }
    ],
    service_provider: [
      { id: 'search', label: 'Search Props', icon: Search },
      { id: 'database', label: 'Database Dashboard', icon: BarChart3 },
      { id: 'service-inventory', label: 'All Inventory', icon: Package },
      { id: 'organizations', label: 'Edit Organizations', icon: Building2 },
      { id: 'categories', label: 'Categories', icon: FolderTree },
      { id: 'ui-setup', label: 'UI Setup', icon: Palette },
      { id: 'setup', label: 'DB Setup', icon: Wrench },
      { id: 'services', label: 'Service Requests', icon: Wrench },
      { id: 'delivery', label: 'Delivery Coordination', icon: Truck },
      { id: 'digitization', label: 'Digitization Queue', icon: Camera }
    ]
  };

  // Show landing page if requested
  if (showLandingPage) {
    return <LandingPage onEnterApp={() => setShowLandingPage(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-md mb-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <button
                onClick={() => setShowLandingPage(true)}
                className="text-base sm:text-xl font-bold text-gray-800 whitespace-nowrap hover:text-blue-600 transition-colors"
              >
                <span className="hidden sm:inline">PropShare Network</span>
                <span className="sm:hidden">PropShare</span>
              </button>
            </div>

            {/* Right side - Controls and User Info */}
            <div className="flex items-center space-x-2 flex-shrink-0 min-w-0">
              {/* Role Selector */}
              <FormGroup.Select
                value={currentUser.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setCurrentUser({...currentUser, role: newRole});
                  
                  if (newRole === 'general') {
                    setActiveView('search');
                  } else if (newRole === 'warehouse_admin') {
                    setActiveView('inventory');
                  } else if (newRole === 'service_provider') {
                    setActiveView('database');
                  }
                }}
                size="compact"
                className="font-medium w-16 sm:w-20"
              >
                <option value="general">General</option>
                <option value="warehouse_admin">Admin</option>
                <option value="service_provider">Service</option>
              </FormGroup.Select>
              
              {/* Organization Selector */}
              <FormGroup.Select
                value={currentUser.organization}
                onChange={(e) => setCurrentUser({...currentUser, organization: e.target.value})}
                size="compact"
                className="font-medium w-20 sm:w-28 md:w-32"
              >
                {organizations.map(org => (
                  <option key={org.id} value={org.name}>
                    {org.name}
                  </option>
                ))}
              </FormGroup.Select>
              
              {/* Notification Center */}
              <div className="flex-shrink-0">
                <NotificationCenter
                  currentUser={currentUser}
                  notifications={notifications}
                  onMarkAsRead={markAsRead}
                  onClearAll={clearAll}
                />
              </div>
              
              {/* Theme Toggle */}
              <div className="flex-shrink-0">
                <ThemeToggle variant="simple" />
              </div>

              {/* Language Toggle */}
              <div className="flex-shrink-0">
                <LanguageToggle />
              </div>

            </div>
          </div>
                    


          {/* Navigation - Now always on second line */}
          <div className="border-t border-gray-200 pt-2 pb-3">
            <div className="flex flex-wrap gap-1">
              {menuItems[currentUser.role]?.map(item => (
                <Button
                  key={item.id}
                  onClick={() => setActiveView(item.id)}
                  variant={activeView === item.id ? 'navActive' : 'nav'}
                  size="compact"
                  icon={item.icon}
                  className="touch-target"
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {renderActiveView()}
      </div>

      <AddPropModal
        showModal={showAddPropModal}
        setShowModal={setShowAddPropModal}
        editingProp={editingProp}
        propForm={propForm}
        setPropForm={setPropForm}
        saveProp={handleSaveProp}
        deleteProp={handleDeleteProp}
      />

      <PropDetailsModal
        prop={selectedProp || recommendedProp}
        onClose={() => {
          setSelectedProp(null);
          setRecommendedProp(null);
        }}
        onAddToCart={addToCart}
        onAddToList={handleAddToList}
        onRequestTransfer={handleRequestTransfer}
        userLists={userLists}
        allProps={props}
        onPropClick={(prop) => {
          // Transition to new prop in same modal
          if (selectedProp) {
            setSelectedProp(prop);
            setRecommendedProp(null);
          } else {
            setRecommendedProp(prop);
          }
        }}
      />

      <TransferModal
        showModal={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        prop={selectedTransferProp}
        currentUser={currentUser}
        onSubmitTransfer={handleTransferSubmit}
      />
      
      {/* Bottom Navigation */}
      <BottomNavigation
        items={menuItems[currentUser.role]?.map(item => ({
          id: item.id,
          icon: item.icon,
          label: item.label.replace(/\s*\(.*\)\s*$/, ''), // Remove count badges
          badge: item.label.includes('(') ? item.label.match(/\((\d+)\)/)?.[1] : null
        })) || []}
        activeItem={activeView}
        onItemClick={(item) => setActiveView(item.id)}
        position="fixed"
      />
    </div>
  );
};

export default PropKioskDashboard;
